import React from "react";
import { Box, Button, Dialog, Grid, Stack, styled, Alert } from "@mui/material";
import MuiTextField from "@mui/material/TextField";
import { Formik } from "formik";
import { useSnackbar } from "notistack";

import * as Yup from "yup";
import { H4 } from "app/components/Typography";
import useZoning from "../hooks/useZoning";

const TextField = styled(MuiTextField)({ marginBottom: 16 });

const validationSchema = Yup.object().shape({
    Code: Yup.string()
        .required("Code is required!")
        .max(2, "Code must be 2 characters or less")
        .matches(/^[a-zA-Z0-9]{1,2}$/, "Code must be 1-2 alphanumeric characters"),
    Description: Yup.string()
        .required("Description is required!")
        .max(99, 'Must be less than 100 characters')
        .matches(/^[a-zA-Z0-9\s-]+$/, "Only letters, numbers, spaces, and hyphens are allowed!"),
});

export default function ZoningEditorDialog({ open, handleClose, zoning }) {

    const {
        roles,
        handleSave,
        showSuccessAlert,
    } = useZoning();
    const isNew = !zoning;
    const { enqueueSnackbar } = useSnackbar();

    const initialValues = {
        ID: zoning?.id || "",
        Code: zoning?.code.trim() || "",
        Description: zoning?.description.trim() || "",
        StatusLUId: zoning?.statusLUId || ""
    };

    const handleFormSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
        let response;
        if (isNew) {
            response = await handleSave(values, isNew, setFieldError);
        }
        else {
            response = await handleSave(values, null, setFieldError);
        }
        setSubmitting(false);
        if (!response) {
            enqueueSnackbar("Zone saved successfully", { variant: "success" });
            resetForm();
            handleClose();
        }
        else {
            enqueueSnackbar("Failed to save zone", { variant: "error" });
        }
    };

    return (
        <Dialog onClose={handleClose} open={open}>
            <Box p={3}>
                <H4 sx={{ mb: "20px" }}>{isNew ? "Add Zone" : "Update Zone"}</H4>
                {showSuccessAlert && (
                    <Alert variant="filled" severity="success">
                        Saved Successfully.
                    </Alert>
                )}
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleFormSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        name="Code"
                                        label="Code"
                                        value={values.Code}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={touched.Code && Boolean(errors.Code)}
                                        helperText={touched.Code && errors.Code}
                                        inputProps={{ maxLength: 2 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={10}>
                                    <TextField
                                        fullWidth
                                        name="Description"
                                        label="Description"
                                        value={values.Description}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={touched.Description && Boolean(errors.Description)}
                                        helperText={touched.Description && errors.Description}
                                    />
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button variant="contained" color="primary" type="submit">
                                    Save
                                </Button>
                                <Button variant="outlined" color="secondary" onClick={handleClose}>
                                    Cancel
                                </Button>
                            </Stack>
                        </form>
                    )}
                </Formik>
            </Box>
        </Dialog>
    );
}
